import styled from "styled-components";
import { FiInfo } from "react-icons/fi";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { Card } from "../Card";
import { H3 } from "../Heading";
import { Paragraph } from "../Paragraph";
import { Tooltip } from "../Tooltip";

/**
 * Who is being looked at, and which record this is, as a header band.
 *
 * Shared shell behind the Monitoring, Screening and Company record header
 * bands (see MonitoringRecordSummary). Horizontal rather than a rail because
 * the content below needs the width: a left rail leaves three columns, and
 * the widest of them is squeezed well below what its content needs.
 *
 * Identity first, record second. "Who am I looking at" precedes "what
 * arrived".
 */

/**
 * Individual or Entity, sat against the name.
 *
 * Same treatment as the segment chips on a match card, so a chip reads as a
 * chip throughout the page. An explicit line-height and vertical padding
 * matter here: without them it sits optically high against a heading three
 * sizes larger.
 */
const SubjectTypeChip = styled(Box)`
  ${({ theme: { colors, fontFamily, fontSizes, fontWeights, radii, space } }) => `
    display: inline-flex;
    align-items: center;
    font-family: ${fontFamily};
    font-size: ${fontSizes[0]};
    font-weight: ${fontWeights.medium};
    line-height: 1;
    letter-spacing: 0.02em;
    color: ${colors.gray[60]};
    background: ${colors.gray[10]};
    border-radius: ${radii[0]};
    padding: ${space[0]} ${space[1]};
    white-space: nowrap;
  `}
`;

/** Label above value, for the field row. An optional tooltip sits beside the
 * label rather than as text under the whole row, so a note that only applies
 * to one field doesn't read as applying to all of them. */
const Field = ({
  label,
  value,
  tooltip,
}: {
  label: string;
  value: React.ReactNode;
  tooltip?: React.ReactNode;
}) => (
  <Box flex={1} minWidth="150px">
    <Flex alignItems="center" gap="4">
      <Paragraph mt={0} mb={0} fontSize={0} color="gray.50">
        {label}
      </Paragraph>
      {tooltip && (
        <Tooltip content={tooltip}>
          <Box
            display="inline-flex"
            alignItems="center"
            color="gray.40"
            style={{ cursor: "help" }}
          >
            <FiInfo size={12} />
          </Box>
        </Tooltip>
      )}
    </Flex>
    <Paragraph mt={0} mb={0} fontSize={1} color="gray.80" fontWeight="500">
      {value === null || value === undefined || value === "" ? "-" : value}
    </Paragraph>
  </Box>
);

export interface SubjectSummaryField {
  label: string;
  value: React.ReactNode;
  /** Shown behind an info icon beside the label, e.g. a retention notice. */
  tooltip?: React.ReactNode;
}

interface Props {
  name: React.ReactNode;
  chipLabel: string;
  /** Facts rendered on one wrapping line, joined by " · ". */
  identityFacts: (string | null | undefined)[];
  /** Rendered before the identity facts line. Entity records only. */
  countryFlag?: string;
  fields: SubjectSummaryField[];
  minWidth?: string;
}

export const SubjectSummaryCard = ({
  name,
  chipLabel,
  identityFacts,
  countryFlag,
  fields,
  minWidth = "420px",
}: Props) => (
  <Card mb={0} p={3} flex={1} minWidth={minWidth}>
    {/* the type sits with the name rather than pushed to the far edge: it
        qualifies the name, and at the other end of a wide card it read as an
        unrelated label */}
    <Flex alignItems="center" gap="8" flexWrap="wrap">
      <H3 fontSize={3} mt={0} mb={0} style={{ wordBreak: "break-word" }}>
        {name || "-"}
      </H3>
      <SubjectTypeChip>{chipLabel}</SubjectTypeChip>
    </Flex>

    <Flex alignItems="center" gap="8" mt={1} flexWrap="wrap">
      {countryFlag && <img src={`data:image/png;base64,${countryFlag}`} alt="" />}
      <Paragraph mt={0} mb={0} fontSize={1} color="gray.60">
        {identityFacts.filter(Boolean).join("  ·  ")}
      </Paragraph>
    </Flex>

    <Flex
      gap="24"
      mt={2}
      pt={1}
      flexWrap="wrap"
      borderTop="1px solid"
      borderTopColor="gray.20"
    >
      {fields.map((field) => (
        <Field
          key={field.label}
          label={field.label}
          value={field.value}
          tooltip={field.tooltip}
        />
      ))}
    </Flex>
  </Card>
);
